import { usePlanningStore } from '@planning/AppProvider'
import { HoldReaction } from '@planning/app/api'
import { HoldEnforcementAlert } from '@planning/components/hold/HoldEnforcementAlert'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import {
  fetchHoldTypes,
  getHoldEnforcementResult,
  masterHoldTypes,
} from '@planning/utils/hold-enforcement'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useEffect } from 'react'
import { ManualGateOutWarningDialog } from './ManualGateOutWarningDialog'

export const GateOutWarningDialogs: FC<{
  viewStore: GateInViewStore
}> = observer(({ viewStore }) => {
  const { t } = useTranslate()
  const { holdItemStore, containerJourneyItemStore } = usePlanningStore()
  const masterTypes = masterHoldTypes.get()

  const isOpen =
    viewStore.isGateOutConfirmationDialogOpen ||
    viewStore.isGateOutWithPendenciesConfirmationDialogOpen

  useEffect(() => {
    if (!isOpen) return
    fetchHoldTypes()
    const journeyIds = viewStore.selectedVisitWithOrders.flatMap(({ order }) => {
      if (!order) return []
      const jId =
        containerJourneyItemStore.journeysByInboundOrderId[order.id]?.id ||
        containerJourneyItemStore.journeysByOutboundOrderId[order.id]?.id ||
        (order.containerId && containerJourneyItemStore.journeysByContainerId[order.containerId]?.id)
      return jId ? [jId] : []
    })
    if (journeyIds.length) holdItemStore.fetchHoldsByContainerJourneyIds(journeyIds)
  }, [isOpen, viewStore.selectedVisitWithOrders, holdItemStore, containerJourneyItemStore])

  const holdResults = viewStore.selectedVisitWithOrders.map(({ order }) => {
    if (!order) return null
    const jId =
      containerJourneyItemStore.journeysByInboundOrderId[order.id]?.id ||
      containerJourneyItemStore.journeysByOutboundOrderId[order.id]?.id ||
      (order.containerId && containerJourneyItemStore.journeysByContainerId[order.containerId]?.id)
    return getHoldEnforcementResult(
      holdItemStore.holdsByContainerJourneyId[jId ?? -1] ?? [],
      'GateOut',
      order.direction,
      masterTypes,
    )
  })

  const holdNames = [...new Set(holdResults.flatMap(r => r?.holdNames ?? []))]
  const holdReaction = holdResults.some(r => r?.isBlocking)
    ? HoldReaction.Block
    : holdResults.some(r => r?.reaction !== null)
      ? HoldReaction.Warning
      : null
  const isBlocked = holdReaction === HoldReaction.Block

  return (
    <>
      <ManualGateOutWarningDialog
        viewStore={viewStore}
        holdReaction={holdReaction}
        holdNames={holdNames}
      />

      <ConfirmationDialog
        title={t('gateOutConfirmation', 'Gate out confirmation')}
        open={viewStore.isGateOutConfirmationDialogOpen}
        onClose={() => viewStore.closeGateOutConfirmationDialog()}
        onConfirm={() => viewStore.gateOut()}
        closeLabel={t('no', 'No')}
        primaryActionText={t('yes', 'Yes')}
        primaryActionDisabled={isBlocked}
        maxWidth='xs'
      >
        {t(
          'truckVisitWillBeFinishedProceedQuestion',
          'This Truck Visit will be finished. Are you sure you want to proceed?',
        )}
        <HoldEnforcementAlert reaction={holdReaction} holdNames={holdNames} />
      </ConfirmationDialog>
    </>
  )
})
